import type { Node, Container } from '@/api/types'
import { useContainers } from '@/hooks/useNodes'
import { useServices } from '@/hooks/useServices'
import ContainerRow from './ContainerRow'
import { Card } from '@/admin-design/_common'
import { cn } from '@/lib/utils'

interface NodeCardProps {
  node: Node
}

const nodeStatusColor: Record<string, string> = {
  online: 'text-success',
  offline: 'text-destructive',
  unknown: 'text-muted-foreground',
}

const nodeStatusDot: Record<string, string> = {
  online: 'bg-success',
  offline: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
}

export default function NodeCard({ node }: NodeCardProps) {
  const { data: containers, isLoading, isFetching, isError } = useContainers(node.id)
  const { data: services } = useServices()

  // Map container name → service id so the row can link into ServiceDetail
  // for registered services (the container-level page offers almost the
  // same info and adds a wasted hop). Non-service containers keep the
  // generic /containers/ route.
  const serviceByName = new Map<string, number>()
  services?.forEach((s) => {
    if (s.node_id === node.id) serviceByName.set(s.name, s.id)
  })

  const sorted = [...(containers ?? [])].sort((a, b) => {
    // running containers first
    if (a.State === 'running' && b.State !== 'running') return -1
    if (a.State !== 'running' && b.State === 'running') return 1
    return (a.Names[0] ?? '').localeCompare(b.Names[0] ?? '')
  })

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'w-2 h-2 rounded-full',
              nodeStatusDot[node.status] ?? 'bg-muted-foreground/40',
            )}
          />
          <h2 className="font-semibold text-card-foreground">{node.name}</h2>
          <span className="text-xs text-muted-foreground">({node.host})</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs font-medium',
              nodeStatusColor[node.status] ?? 'text-muted-foreground',
            )}
          >
            {node.status}
          </span>
          {isFetching && !isLoading && (
            <span className="text-[10px] text-muted-foreground animate-pulse">
              refreshing...
            </span>
          )}
        </div>
      </div>

      {/* Container list */}
      <div className="divide-y divide-border">
        {isLoading && (
          <div className="px-4 py-6">
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 bg-muted rounded animate-pulse"
                />
              ))}
            </div>
          </div>
        )}

        {isError && (
          <div className="px-4 py-4">
            <p className="text-sm text-destructive">
              Failed to load containers. The node may be unreachable.
            </p>
          </div>
        )}

        {!isLoading && !isError && sorted.length === 0 && (
          <div className="px-4 py-4">
            <p className="text-sm text-muted-foreground">No containers found.</p>
          </div>
        )}

        {sorted.map((c: Container) => {
          const cleanName = (c.Names[0] ?? '').replace(/^\//, '')
          const svcId = serviceByName.get(cleanName)
          return (
            <ContainerRow
              key={c.Id}
              container={c}
              nodeId={node.id}
              linkTo={svcId !== undefined ? `/services/${svcId}` : undefined}
            />
          )
        })}
      </div>
    </Card>
  )
}
